<script lang="ts">
  import Box, { type BoxProps } from '../Box/Box.vue';
  import { ModuleVariant, type ModuleVariants } from './Module.types';

  export * from './Module.keys';
  export * from './Module.types';

  export interface ModuleProps extends BoxProps {
    /**
     * Variant applied.
     *
     * Options: `box`, `card`, `list`.
     */
    variant?: ModuleVariants;

    /**
     * Whether to include dataview pagination within the module.
     * By default the bottom pagination is not included.
     */
    includeDataviewPagination?: boolean;
  }
</script>

<script setup lang="ts">
  import logger from '@leaflink/snitch';
  import { computed, onMounted, provide } from 'vue';

  import { MODULE_INJECTION } from './Module.keys';

  defineOptions({
    name: 'll-module',
  });

  const props = withDefaults(defineProps<ModuleProps>(), {
    variant: 'box',
    includeDataviewPagination: false,
  });

  onMounted(() => {
    if (props.variant === 'card') {
      logger.warn('Card Modules are no longer a thing. You\'re probably looking for `<RadioGroup variant="card">`.');
    }

    if (props.variant === 'list') {
      logger.warn('Only used with deprecated ListView component. Use `table` variant with `Table` component instead.');
    }
  });

  provide(MODULE_INJECTION.key, {
    variant: computed(() => props.variant),
    includeDataviewPagination: computed(() => props.includeDataviewPagination),
  });
</script>

<template>
  <Box
    class="stash-module flex flex-col"
    data-test="stash-module"
    :class="{
      'bg-transparent shadow': props.variant === ModuleVariant.Table,
      'border border-ice-500': props.variant === ModuleVariant.Card,
      'mb-6 bg-transparent shadow-none lg:shadow': props.variant === ModuleVariant.List,
    }"
    :disable-gutters="props.disableGutters"
    :disable-padding="props.disablePadding || ['card', 'list', 'table'].includes(props.variant)"
    :disable-elevation="
      props.disableElevation ||
      props.variant === ModuleVariant.Table ||
      props.variant === ModuleVariant.Card ||
      props.variant === ModuleVariant.List
    "
  >
    <slot></slot>
  </Box>
</template>
